import type { NegotiationReview } from '@shared/domain/review';
import { Box, Checkbox, FormControlLabel, FormGroup, FormHelperText, Link, Typography } from '@mui/material';
import React, { useState } from 'react';
import { ReviewStep } from '@shared/domain/review';
import { CenteredDiv } from '@shared/styles/FormStyles';
import { NEGOTIATION_FORM_FIELDS } from '../../data';
import { getReviewStepName } from '../../helpers/ReviewStepNames';
import { useReviewForm } from '../../hooks/useReviewForm';
import { Container, SaveBar, SaveButton } from '../../styles/review.styles';
import { QuestionRenderer } from '../QuestionRenderer';

interface NegotiationReviewProps {
  submitReviewForm?: (
    formFields: NegotiationReview,
    reviewStep: ReviewStep,
    isDraft?: boolean,
  ) => void;
  rfp?: boolean;
  editMode?: boolean;
  defaultValues?: Partial<NegotiationReview> & { id?: number };
  handleEditSuccess?: () => void;
}

function NegotiationReviewForm(props: NegotiationReviewProps) {
  const { submitReviewForm, rfp, editMode, defaultValues, handleEditSuccess } = props;
  const [termsAgreed, setTermsAgreed] = useState(false);

  const {
    formFields,
    extendFormFields,
    setFormFields,
    errors,
    validateField,
    handleFormFieldChange,
    handleSubmit,
    handleEdit,
  } = useReviewForm<NegotiationReview>({
    reviewStep: ReviewStep.NEGOTIATION,
    formFields: NEGOTIATION_FORM_FIELDS,
    editMode,
    defaultValues,
    onSubmit: submitReviewForm,
    onEditSuccess: handleEditSuccess,
    customValidationFields: (!rfp && !editMode) ? { termsAgreed } : {},
  });

  const buttonText = rfp ? 'Continue onto RFP' : 'Submit Deal Retro';

  const handleTermsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setTermsAgreed(checked);
    validateField('termsAgreed', checked);
  };

  return (
    <Container>
      <div data-testid="negotiation-review-step-content-anchor" />
      <form>
        <QuestionRenderer
          questions={NEGOTIATION_FORM_FIELDS}
          formFields={formFields}
          errors={errors}
          handleFormFieldChange={handleFormFieldChange}
          extendFormFields={extendFormFields}
          validateField={validateField}
          setFormFields={setFormFields}
        />
        {editMode
          ? (
              <SaveBar>
                <SaveButton onClick={handleEdit}>{`Save ${getReviewStepName(ReviewStep.NEGOTIATION)} Changes`}</SaveButton>
              </SaveBar>
            )
          : (
              <>
                {!rfp && (
                  <Box sx={{ mb: 2, alignItems: 'center', display: 'flex', alignContent: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, borderRadius: '12px', background: '#fff', boxShadow: '0 8px 24px rgba(16, 24, 40, 0.06)', p: 2 }}>
                    <Typography variant="body1">
                      By submitting this deal retro, you agree to our
                      {' '}
                      <Link href="https://www.kandir.io/terms">terms and conditions</Link>
                      .
                    </Typography>
                    <div>
                      <FormGroup data-error-name="termsAgreed">
                        <FormControlLabel
                          control={(
                            <Checkbox
                              data-testid="terms-agree-checkbox"
                              checked={termsAgreed}
                              onChange={handleTermsChange}
                            />
                          )}
                          label="I Agree"
                        />
                        {errors.termsAgreed && (
                          <FormHelperText error>{errors.termsAgreed}</FormHelperText>
                        )}
                      </FormGroup>
                    </div>
                  </Box>
                )}
                <CenteredDiv>
                  <SaveButton onClick={handleSubmit}>
                    {buttonText}
                  </SaveButton>
                </CenteredDiv>
              </>
            )}
      </form>
    </Container>
  );
}

export default NegotiationReviewForm;
